import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Divider } from '@magnetic/divider'
import { Notification } from '@magnetic/notification'
import { Tag } from '@magnetic/tag'
import { Text } from '@magnetic/text'
import { Package, Clock, CheckCircle } from '@/lib/icons'

interface DeploymentStatusProps {
  currentImageTag?: string
  currentImageDigest?: string
  lastDeployedAt?: string
  ecrRepository?: string
  imageDeploymentStatus?: string
  imageDeploymentMessage?: string
  tagTrackingEnabled?: boolean
  trackedImageTag?: string
  trackedImageDigest?: string
  tagTrackingMode?: string
}

export function DeploymentStatus({
  currentImageTag,
  currentImageDigest,
  lastDeployedAt,
  ecrRepository,
  imageDeploymentStatus,
  imageDeploymentMessage,
  tagTrackingEnabled,
  trackedImageTag,
  trackedImageDigest,
  tagTrackingMode
}: DeploymentStatusProps) {
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isConfigured = !!ecrRepository
  const hasDeploymentError = imageDeploymentStatus === 'failed'
  const trackedTagDiffers = !!trackedImageTag && trackedImageTag !== currentImageTag
  const trackedDigestDiffers = !!trackedImageDigest && !!currentImageDigest && trackedImageDigest !== currentImageDigest
  const updateAvailable = !!tagTrackingEnabled && (trackedTagDiffers || trackedDigestDiffers)

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Flex align="center" gap="sm">
          <Package size={20} />
          <Heading size="sub-section">Current Deployment</Heading>
        </Flex>

        {!isConfigured ? (
          <Notification status="information" title="Image deployment not configured">
            <Text size="sm">
              Configure the ECR repository to enable image deployments.
            </Text>
          </Notification>
        ) : (
          <Flex direction="vertical" gap="md">
            {hasDeploymentError && (
              <Notification status="negative" title="ECR deployment failed">
                <Text size="sm">{imageDeploymentMessage || 'Image could not be pushed to ECR.'}</Text>
              </Notification>
            )}

            {currentImageTag ? (
              <>
                <Flex align="center" gap="sm">
                  <CheckCircle size={20} style={{ color: 'var(--mds-color-theme-text-success-normal)' }} />
                  <Flex direction="vertical">
                    <Text size="sm" color="light">Current Image Tag</Text>
                    <Tag>{currentImageTag}</Tag>
                  </Flex>
                </Flex>

                {currentImageDigest && (
                  <Flex direction="vertical" gap="xs">
                    <Text size="sm" color="light">Image Digest</Text>
                    <Text size="xs" style={{ wordBreak: 'break-all' }}>
                      {currentImageDigest}
                    </Text>
                  </Flex>
                )}

                {lastDeployedAt && (
                  <Flex align="center" gap="sm" style={{ opacity: 0.7 }}>
                    <Clock size={16} />
                    <Text size="sm">Last deployed: {formatDate(lastDeployedAt)}</Text>
                  </Flex>
                )}

                <Divider />
                <Flex direction="vertical" gap="sm">
                  <Flex direction="vertical" gap="xs">
                    <Text size="xs" color="light">ECR Repository</Text>
                    <Tag>{ecrRepository || 'Not set'}</Tag>
                  </Flex>
                  {tagTrackingEnabled && trackedImageTag && (
                    <Flex direction="vertical" gap="xs">
                      <Text size="xs" color="light">Tracked Tag</Text>
                      <Flex align="center" gap="sm" wrap>
                        <Tag>{trackedImageTag}</Tag>
                        <Text size="xs" color="light">
                          ({tagTrackingMode === 'auto-deploy' ? 'auto-deploy' : 'notify-only'})
                        </Text>
                        {updateAvailable && (
                          <Tag.Status status="warning">Update available</Tag.Status>
                        )}
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </>
            ) : (
              <Notification status="information" title="No deployment yet">
                <Text size="sm">Deploy an image tag to get started.</Text>
              </Notification>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
